import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Investment, InvestmentResults, WhatIfScenarioParams, ScenarioType } from '../types';
import { calculateResults } from '../utils/calculations';
import { convertToMXN } from '../utils/formatters';

// Chart visibility interfaces
export interface PatrimonyChartVisibility {
  cosechaCapital: boolean;
  contributedCapital: boolean;
  cetes: boolean;
  savings: boolean;
  realEstate: boolean;
  showReal: boolean;
  ebitda: boolean;
}

export interface IncomeChartVisibility {
  cosechaCapital: boolean;
  cetes: boolean;
  savings: boolean;
  realEstate: boolean;
  showReal: boolean;
  showTaxes: boolean;
}

export interface CashFlowChartVisibility {
  cosechaCapital: boolean;
  cetes: boolean;
  savings: boolean;
  realEstate: boolean;
  cumulativeFlow: boolean;
  showPartialCashOut: boolean;
}

export interface ComparisonChartVisibility {
  cosechaCapital: boolean;
  cetes: boolean;
  savings: boolean;
  realEstate: boolean;
  showReal: boolean;
  showCustom: boolean;
}

export interface WithdrawalPlanChartVisibility {
  showPercentages: boolean;
  showAmounts: boolean;
  showCumulativeRetirement: boolean;
  showCumulativeReinvestment: boolean;
}

interface CalculatorContextType {
  investment: Investment;
  results: InvestmentResults | null;
  updateInvestment: (updates: Partial<Investment>) => void;
  toggleReinvest: () => void;
  toggleContributions: () => void;
  toggleLemonPrice: () => void;
  toggleTaxes: () => void;
  togglePartialCashOut: () => void;
  updateCashOutPercentage: (percentage: number) => void;
  updateYearlyCashOutPercentage: (year: number, percentage: number) => void;
  resetYearlyCashOutPercentages: () => void;
  setDefaultYearlyCashOutPercentage: (percentage: number) => void;
  resetCalculator: () => void;
  setUtilityScenario: (scenario: 'conservative' | 'moderate' | 'optimistic') => void;

  // What-if scenario functionality
  whatIfScenario: WhatIfScenarioParams | null;
  whatIfResults: InvestmentResults | null;
  setWhatIfScenario: (scenario: WhatIfScenarioParams) => void;
  clearWhatIfScenario: () => void;
  applyPredefinedScenario: (scenarioType: ScenarioType) => void;

  // Chart visibility controls
  patrimonyChartVisibility: PatrimonyChartVisibility;
  incomeChartVisibility: IncomeChartVisibility;
  cashFlowChartVisibility: CashFlowChartVisibility;
  comparisonChartVisibility: ComparisonChartVisibility;
  withdrawalPlanChartVisibility: WithdrawalPlanChartVisibility;

  togglePatrimonyChartElement: (element: keyof PatrimonyChartVisibility) => void;
  toggleIncomeChartElement: (element: keyof IncomeChartVisibility) => void;
  toggleCashFlowChartElement: (element: keyof CashFlowChartVisibility) => void;
  toggleComparisonChartElement: (element: keyof ComparisonChartVisibility) => void;
  toggleWithdrawalPlanChartElement: (element: keyof WithdrawalPlanChartVisibility) => void;

  resetPatrimonyChartVisibility: () => void;
  resetIncomeChartVisibility: () => void;
  resetCashFlowChartVisibility: () => void;
  resetComparisonChartVisibility: () => void;
  resetWithdrawalPlanChartVisibility: () => void;

  // Presentation mode
  presentationMode: boolean;
  togglePresentationMode: () => void;
  savedScenarios: Array<{ id: string; name: string; investment: Investment }>;
  saveScenario: (name: string) => void;
  loadScenario: (id: string) => void;
  deleteScenario: (id: string) => void;
}

const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined);

const defaultInvestment: Investment = {
  initialCertificates: 1,
  certificateBasePrice: 266000,
  initialPayment: 266000, // Set to full certificate price by default
  years: 25,
  annualProfit: 20,
  increaseLemonPrice: true,
  lemonPriceIncrease: 5,
  reinvestProfits: true,
  additionalContributions: false,
  monthlyContribution: 10000,
  currencyFormat: 'MXN',
  exchangeRate: 20,
  exchangeRateEUR: 21.70,
  inflationRate: 5,
  applyTaxes: false,
  taxRate: 30,
  partialCashOut: false,
  cashOutPercentage: 30,
  yearlyCashOutPercentages: Array(30).fill(30),
  appreciationRate: 12,
  cetesRate: 7.5,
  savingsRate: 4.0,
  realEstateRate: 8.0,
  realEstateAppreciation: 8,
  realEstateRent: 6,
  ebitdaFactor: 10,
  averageProductionPerHectare: 35000,
  averageSalePricePerKg: 38,
  isLongTermCalculator: false,
  firstYearUtilityToUser: false,
  commissionRate: 0.05,
  citrusReinvestment: true,
  citrusReinvestmentPercentages: Array(30).fill(100),
  enablePaymentBoost: false,
  paymentBoostAmount: undefined,
  paymentBoostGrowthRate: 0,
  paymentBoostYears: undefined,
  investorFactor: 65,
  // New payment calculator properties
  investorName: '',
  investorPhone: '',
  investorEmail: '',
  executiveName: '',
  executivePhone: '',
  executiveEmail: '',
  downPaymentPercentage: 30,
  enableCustomPayments: false,
  customPaymentSchedule: [],
  financingInterestRate: 0,
  enableCustomDownPaymentSchedule: false,
  customDownPaymentSchedule: [],
  downPaymentInstallments: 1,
  customInvestmentRate: 8,
  customInvestmentName: 'Mi Inversión',
  investorAnnualReturn: 20,
  investorPriceAppreciation: 12,
  financingDownPaymentPercent: 30,
  financingAnnualInterestRate: 12
};

// Default chart visibility states
const defaultPatrimonyChartVisibility: PatrimonyChartVisibility = {
  cosechaCapital: true,
  contributedCapital: true,
  cetes: true,
  savings: true,
  realEstate: true,
  showReal: false,
  ebitda: true
};

const defaultIncomeChartVisibility: IncomeChartVisibility = {
  cosechaCapital: true,
  cetes: true,
  savings: true,
  realEstate: true,
  showReal: false,
  showTaxes: true
};

const defaultCashFlowChartVisibility: CashFlowChartVisibility = {
  cosechaCapital: true,
  cetes: true,
  savings: true,
  realEstate: true,
  cumulativeFlow: true,
  showPartialCashOut: true
};

const defaultComparisonChartVisibility: ComparisonChartVisibility = {
  cosechaCapital: true,
  cetes: true,
  savings: true,
  realEstate: true,
  showReal: false,
  showCustom: true
};

const defaultWithdrawalPlanChartVisibility: WithdrawalPlanChartVisibility = {
  showPercentages: true,
  showAmounts: true,
  showCumulativeRetirement: true,
  showCumulativeReinvestment: true
};

export const CalculatorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [investment, setInvestment] = useState<Investment>(defaultInvestment);
  const [results, setResults] = useState<InvestmentResults | null>(null);
  const [whatIfScenario, setWhatIfScenarioState] = useState<WhatIfScenarioParams | null>(null);
  const [whatIfResults, setWhatIfResults] = useState<InvestmentResults | null>(null);

  // Chart visibility states
  const [patrimonyChartVisibility, setPatrimonyChartVisibility] = useState<PatrimonyChartVisibility>(defaultPatrimonyChartVisibility);
  const [incomeChartVisibility, setIncomeChartVisibility] = useState<IncomeChartVisibility>(defaultIncomeChartVisibility);
  const [cashFlowChartVisibility, setCashFlowChartVisibility] = useState<CashFlowChartVisibility>(defaultCashFlowChartVisibility);
  const [comparisonChartVisibility, setComparisonChartVisibility] = useState<ComparisonChartVisibility>(defaultComparisonChartVisibility);
  const [withdrawalPlanChartVisibility, setWithdrawalPlanChartVisibility] = useState<WithdrawalPlanChartVisibility>(defaultWithdrawalPlanChartVisibility);

  // Presentation mode states
  const [presentationMode, setPresentationMode] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState<Array<{ id: string; name: string; investment: Investment }>>([]);

  // Calculate results whenever investment changes
  useEffect(() => {
    try {
      const newResults = calculateResults(investment);
      setResults(newResults);
    } catch (error) {
      console.error('Error calculating results:', error);
      setResults(null);
    }
  }, [investment]);

  // Calculate what-if results when scenario changes
  useEffect(() => {
    if (whatIfScenario) {
      try {
        // Create a modified investment with what-if parameters
        const modifiedInvestment: Investment = {
          ...investment,
          averageProductionPerHectare: whatIfScenario.averageProductionPerHectare || investment.averageProductionPerHectare,
          averageSalePricePerKg: whatIfScenario.averageSalePricePerKg || investment.averageSalePricePerKg,
          lemonPriceIncrease: whatIfScenario.lemonPriceIncrease || investment.lemonPriceIncrease,
          inflationRate: whatIfScenario.inflationRate || investment.inflationRate,
          cetesRate: whatIfScenario.cetesRate || investment.cetesRate,
          savingsRate: whatIfScenario.savingsRate || investment.savingsRate,
          realEstateAppreciation: whatIfScenario.realEstateAppreciation || investment.realEstateAppreciation,
          realEstateRent: whatIfScenario.realEstateRent || investment.realEstateRent,
          ebitdaFactor: whatIfScenario.ebitdaFactor || investment.ebitdaFactor
        };
        
        const newWhatIfResults = calculateResults(modifiedInvestment);
        setWhatIfResults(newWhatIfResults);
      } catch (error) {
        console.error('Error calculating what-if results:', error);
        setWhatIfResults(null);
      }
    } else {
      setWhatIfResults(null);
    }
  }, [whatIfScenario, investment]);

  const updateInvestment = (updates: Partial<Investment>) => {
    setInvestment(prev => ({ ...prev, ...updates }));
  };

  const toggleReinvest = () => {
    setInvestment(prev => ({ ...prev, reinvestProfits: !prev.reinvestProfits }));
  };

  const toggleContributions = () => {
    setInvestment(prev => ({ ...prev, additionalContributions: !prev.additionalContributions }));
  };

  const toggleLemonPrice = () => {
    setInvestment(prev => ({ ...prev, increaseLemonPrice: !prev.increaseLemonPrice }));
  };

  const toggleTaxes = () => {
    setInvestment(prev => ({ ...prev, applyTaxes: !prev.applyTaxes }));
  };

  const togglePartialCashOut = () => {
    setInvestment(prev => ({ ...prev, partialCashOut: !prev.partialCashOut }));
  };

  const updateCashOutPercentage = (percentage: number) => {
    setInvestment(prev => ({ ...prev, cashOutPercentage: percentage }));
  };

  const updateYearlyCashOutPercentage = (year: number, percentage: number) => {
    setInvestment(prev => {
      const newPercentages = [...prev.yearlyCashOutPercentages];
      newPercentages[year - 1] = percentage;
      return { ...prev, yearlyCashOutPercentages: newPercentages };
    });
  };

  const resetYearlyCashOutPercentages = () => {
    setInvestment(prev => ({
      ...prev,
      yearlyCashOutPercentages: Array(30).fill(prev.cashOutPercentage)
    }));
  };

  const setDefaultYearlyCashOutPercentage = (percentage: number) => {
    setInvestment(prev => ({
      ...prev,
      cashOutPercentage: percentage,
      yearlyCashOutPercentages: Array(30).fill(percentage)
    }));
  };

  const resetCalculator = () => {
    setInvestment(defaultInvestment);
    setResults(null);
    setWhatIfScenarioState(null);
    setWhatIfResults(null);
  };

  const setUtilityScenario = (scenario: 'conservative' | 'moderate' | 'optimistic') => {
    const scenarios = {
      conservative: { production: 25566, price: 28 },
      moderate: { production: 27341, price: 32 },
      optimistic: { production: 28259, price: 38 }
    };
    
    updateInvestment({ 
      averageProductionPerHectare: scenarios[scenario].production,
      averageSalePricePerKg: scenarios[scenario].price
    });
  };

  // What-if scenario functions
  const setWhatIfScenario = (scenario: WhatIfScenarioParams) => {
    setWhatIfScenarioState(scenario);
  };

  const clearWhatIfScenario = () => {
    setWhatIfScenarioState(null);
    setWhatIfResults(null);
  };

  const applyPredefinedScenario = (scenarioType: ScenarioType) => {
    const scenarios: Record<ScenarioType, WhatIfScenarioParams> = {
      optimistic: {
        name: "Escenario Optimista",
        description: "Condiciones de mercado favorables con alto crecimiento y baja inflación",
        averageProductionPerHectare: 35000,
        averageSalePricePerKg: 45,
        lemonPriceIncrease: 8,
        inflationRate: 2,
        cetesRate: 12,
        savingsRate: 8,
        realEstateAppreciation: 12,
        realEstateRent: 8,
        ebitdaFactor: investment.ebitdaFactor
      },
      pessimistic: {
        name: "Escenario Pesimista",
        description: "Condiciones de mercado desfavorables con bajo crecimiento y alta inflación",
        averageProductionPerHectare: 20000,
        averageSalePricePerKg: 25,
        lemonPriceIncrease: 2,
        inflationRate: 10,
        cetesRate: 6,
        savingsRate: 3,
        realEstateAppreciation: 4,
        realEstateRent: 3,
        ebitdaFactor: investment.ebitdaFactor
      },
      crisis: {
        name: "Crisis Económica",
        description: "Escenario de crisis económica severa con alta inflación y rendimientos reducidos",
        averageProductionPerHectare: 18000,
        averageSalePricePerKg: 22,
        lemonPriceIncrease: 1,
        inflationRate: 15,
        cetesRate: 4,
        savingsRate: 2,
        realEstateAppreciation: 2,
        realEstateRent: 2,
        ebitdaFactor: investment.ebitdaFactor
      },
      boom: {
        name: "Boom Económico",
        description: "Escenario de auge económico con crecimiento excepcional y baja inflación",
        averageProductionPerHectare: 38000,
        averageSalePricePerKg: 50,
        lemonPriceIncrease: 10,
        inflationRate: 1,
        cetesRate: 15,
        savingsRate: 10,
        realEstateAppreciation: 15,
        realEstateRent: 10,
        ebitdaFactor: investment.ebitdaFactor
      },
      inflation: {
        name: "Inflación Alta",
        description: "Escenario de inflación elevada sostenida con ajustes en tasas de interés",
        averageProductionPerHectare: investment.averageProductionPerHectare,
        averageSalePricePerKg: investment.averageSalePricePerKg,
        lemonPriceIncrease: investment.lemonPriceIncrease,
        inflationRate: 12,
        cetesRate: 15,
        savingsRate: 8,
        realEstateAppreciation: 10,
        realEstateRent: 7,
        ebitdaFactor: investment.ebitdaFactor
      }
    };

    setWhatIfScenario(scenarios[scenarioType]);
  };

  // Chart visibility toggle functions
  const togglePatrimonyChartElement = (element: keyof PatrimonyChartVisibility) => {
    setPatrimonyChartVisibility(prev => ({
      ...prev,
      [element]: !prev[element]
    }));
  };

  const toggleIncomeChartElement = (element: keyof IncomeChartVisibility) => {
    setIncomeChartVisibility(prev => ({
      ...prev,
      [element]: !prev[element]
    }));
  };

  const toggleCashFlowChartElement = (element: keyof CashFlowChartVisibility) => {
    setCashFlowChartVisibility(prev => ({
      ...prev,
      [element]: !prev[element]
    }));
  };

  const toggleComparisonChartElement = (element: keyof ComparisonChartVisibility) => {
    setComparisonChartVisibility(prev => ({
      ...prev,
      [element]: !prev[element]
    }));
  };

  const toggleWithdrawalPlanChartElement = (element: keyof WithdrawalPlanChartVisibility) => {
    setWithdrawalPlanChartVisibility(prev => ({
      ...prev,
      [element]: !prev[element]
    }));
  };

  // Reset chart visibility functions
  const resetPatrimonyChartVisibility = () => {
    setPatrimonyChartVisibility(defaultPatrimonyChartVisibility);
  };

  const resetIncomeChartVisibility = () => {
    setIncomeChartVisibility(defaultIncomeChartVisibility);
  };

  const resetCashFlowChartVisibility = () => {
    setCashFlowChartVisibility(defaultCashFlowChartVisibility);
  };

  const resetComparisonChartVisibility = () => {
    setComparisonChartVisibility(defaultComparisonChartVisibility);
  };

  const resetWithdrawalPlanChartVisibility = () => {
    setWithdrawalPlanChartVisibility(defaultWithdrawalPlanChartVisibility);
  };

  const togglePresentationMode = () => {
    setPresentationMode(prev => !prev);
  };

  const saveScenario = (name: string) => {
    const newScenario = {
      id: Date.now().toString(),
      name,
      investment: { ...investment }
    };
    setSavedScenarios(prev => [...prev, newScenario]);
  };

  const loadScenario = (id: string) => {
    const scenario = savedScenarios.find(s => s.id === id);
    if (scenario) {
      setInvestment(scenario.investment);
    }
  };

  const deleteScenario = (id: string) => {
    setSavedScenarios(prev => prev.filter(s => s.id !== id));
  };

  const value: CalculatorContextType = {
    investment,
    results,
    updateInvestment,
    toggleReinvest,
    toggleContributions,
    toggleLemonPrice,
    toggleTaxes,
    togglePartialCashOut,
    updateCashOutPercentage,
    updateYearlyCashOutPercentage,
    resetYearlyCashOutPercentages,
    setDefaultYearlyCashOutPercentage,
    resetCalculator,
    setUtilityScenario,
    whatIfScenario,
    whatIfResults,
    setWhatIfScenario,
    clearWhatIfScenario,
    applyPredefinedScenario,
    patrimonyChartVisibility,
    incomeChartVisibility,
    cashFlowChartVisibility,
    comparisonChartVisibility,
    withdrawalPlanChartVisibility,
    togglePatrimonyChartElement,
    toggleIncomeChartElement,
    toggleCashFlowChartElement,
    toggleComparisonChartElement,
    toggleWithdrawalPlanChartElement,
    resetPatrimonyChartVisibility,
    resetIncomeChartVisibility,
    resetCashFlowChartVisibility,
    resetComparisonChartVisibility,
    resetWithdrawalPlanChartVisibility,
    presentationMode,
    togglePresentationMode,
    savedScenarios,
    saveScenario,
    loadScenario,
    deleteScenario
  };

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
};

export const useCalculator = (): CalculatorContextType => {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
};